import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { useTours } from 'features/tour/hooks/useTours';
import { usePrefetchTours } from 'features/tour/hooks/usePrefetchTours';
import { useURLSync } from 'features/tour/hooks/useURLSync';
import { ViewTourModal } from 'features/tour/components/ViewTourModal';
import { EditTourModal } from 'features/tour/components/EditTourModal';
import { DeleteConfirmModal } from 'features/tour/components/DeleteConfirmModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { tourServices } from 'features/tour/services/tour.service';


import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Search,
  Trash2,
  X,
  MapPin,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDateTime } from 'utils/dateFormatter';

const TourManagement = () => {
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    sellerId: '',
    destination: '',
    tourType: 'all',
    category: 'all',
    approvalStatus: 'all',
    isActive: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);

  const { data, isPending, isError, error } = useTours(filters);

  const { data: profileData } = useUserProfile();
const user = profileData?.data;

const restrictedRoles = [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "finance_manager",
];

const showPermissionError = restrictedRoles.includes(
  user?.role?.name
);


  const { prefetchNextPage, prefetchPrevPage } = usePrefetchTours();

  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
    }));
  };

const tours = data?.data || [];

const handleExport = async () => {
  try {
    const response =
      await tourServices.exportTours({
        destination:
          filters.destination,

        tourType:
          filters.tourType,

        category:
          filters.category,

        approvalStatus:
          filters.approvalStatus,

        isActive:
          filters.isActive,

        search:
          filters.search,

        sortBy:
          filters.sortBy,

        sortOrder:
          filters.sortOrder,
      });

    const blob = new Blob(
      [response.data],
      {
        type: 'text/csv',
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download = `tours-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export tours',
      error
    );
  }
};

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (tour) => {
    setIsEditModalOpen(true);
    setSelectedTour(tour);
  };

  const handleDeleteClick = (tour) => {
    setSelectedTour(tour);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (tour) => {
    setSelectedTour(tour);
    setIsViewModalOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
      case 'rejected':
        return 'bg-red-500/10 text-red-600 dark:text-red-400';
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
    }
  };

  const getActiveStatusColor = (isActive) => {
    return isActive
      ? 'bg-green-500/10 text-green-600 dark:text-green-400'
      : 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  return (
    <Layout>
      <div className='min-h-screen bg-background text-foreground p-6'>
        <div className={PAGE_CONTENT_WIDTH}>
          {/* Header */}
          <PageHeader
            icon={MapPin}
            title="Tour Management"
            subtitle="Manage tour packages and approvals"
            actions={
              !showPermissionError && (
                <button
                  onClick={handleExport}
                  className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              )
            }
          />

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6'>
            {/* Search */}
            <div className='relative lg:col-span-2'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                size={16}
              />
              <input
                type='text'
                placeholder='Search tours, destinations, sellers...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Tour Type Filter */}
            <select
              value={filters.tourType}
              onChange={(e) => updateFilter('tourType', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Types</option>
              <option value='group'>Group</option>
              <option value='private'>Private</option>
              <option value='custom'>Custom</option>
            </select>

            {/* Approval Status Filter */}
            <select
              value={filters.approvalStatus}
              onChange={(e) => updateFilter('approvalStatus', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Approvals</option>
              <option value='pending'>Pending</option>
              <option value='approved'>Approved</option>
              <option value='rejected'>Rejected</option>
            </select>

            {/* Active Status Filter */}
            <select
              value={filters.isActive}
              onChange={(e) => updateFilter('isActive', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Status</option>
              <option value='true'>Active</option>
              <option value='false'>Inactive</option>
            </select>

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter('limit', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='5'>5 per page</option>
              <option value='10'>10 per page</option>
              <option value='20'>20 per page</option>
              <option value='50'>50 per page</option>
            </select>
          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Image
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('title')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Tour Title</span>
                        <SortIcon column='title' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Seller
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Destination
                    </th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Type
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Duration
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Price
                    </th> */}
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Approval
                    </th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Status
                    </th> */}
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Bookings
                    </th> */}
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Created</span>
                        <SortIcon column='createdAt' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {showPermissionError ? (
    <tr>
      <td
        colSpan={12}
        className='px-4 py-8 text-center text-destructive'
      >
        Error loading tours: You do not have permission to perform this action!
      </td>
    </tr>
  ) : isPending ? (
                    <tr>
                      <td
                        colSpan={12}
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        Loading tours...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan={12}
                        className='px-4 py-8 text-center text-destructive'
                      >
                        Error loading tours:{' '}
                        {error?.message || 'Something went wrong'}
                      </td>
                    </tr>
                  ) : data?.data?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        No tours found
                      </td>
                    </tr>
                  ) : (
                    data?.data?.map((tour) => (
                      <tr
                        key={tour._id}
                        className='hover:bg-muted/50 transition-colors'
                      >
                        {/* Image Column */}
                        <td className='px-4 py-3'>
                          {tour.images && tour.images.length > 0 ? (
                            <img
                              src={tour.images[tour.images.length - 1]?.url || tour.images[tour.images.length - 1]}
                              alt={tour.title}
                              className='w-12 h-12 rounded-lg object-cover border border-border'
                            />
                          ) : (
                            <div className='w-12 h-12 rounded-lg bg-muted flex items-center justify-center'>
                              <MapPin
                                size={20}
                                className='text-muted-foreground'
                              />
                            </div>
                          )}
                        </td>

                        {/* Title Column */}
                        <td className='px-4 py-3 text-sm font-medium'>
                          <div className='max-w-xs'>
                            <div className='text-foreground truncate'>
                              {tour.title}
                            </div>
                            {tour.slug && (
                              <div className='text-xs text-muted-foreground truncate'>
                                {tour.slug}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Seller Column */}
                        <td className='px-4 py-3 text-sm'>
                          <div className='max-w-xs truncate'>
                            {tour.sellerId?.firstName || tour.sellerId?.lastName
                              ? `${tour.sellerId?.firstName || ''} ${tour.sellerId?.lastName || ''}`.trim()
                              : tour.sellerId?.phoneNumber || 'N/A'}
                          </div>
                        </td>

                        {/* Destination Column */}
                        <td className='px-4 py-3 text-sm'>
                          <div className='flex items-center gap-1'>
                            <MapPin
                              size={14}
                              className='text-muted-foreground'
                            />
                            <span className='truncate max-w-[150px]'>
                              {tour.destination}
                            </span>
                          </div>
                        </td>

                        {/* Type Column */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <span className='px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium capitalize whitespace-nowrap'>
                            {tour.tourType}
                          </span>
                        </td> */}

                        {/* Duration Column */}
                        {/* <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {tour.duration} {tour.duration === 1 ? 'day' : 'days'}
                        </td> */}

                        {/* Price Column */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <div>
                            <div className='font-medium text-foreground whitespace-nowrap'>
                              {tour.pricing?.currency || 'INR'}{' '}
                              {tour.pricing?.basePrice?.toFixed(2) || '0.00'}
                            </div>
                            {tour.pricing?.discountedPrice &&
                              tour.pricing?.discountedPrice <
                                tour.pricing?.basePrice && (
                                <div className='text-xs text-muted-foreground line-through'>
                                  {tour.pricing?.currency || 'INR'}{' '}
                                  {tour.pricing?.discountedPrice?.toFixed(2)}
                                </div>
                              )}
                          </div>
                        </td> */}

                        {/* Approval Status Column */}
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                              tour.approvalStatus,
                            )}`}
                          >
                            {tour.approvalStatus}
                          </span>
                        </td>

                        {/* Active Status Column */}
                        {/* <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getActiveStatusColor(
                              tour.isActive,
                            )}`}
                          >
                            {tour.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td> */}

                        {/* Bookings Column */}
                        {/* <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {tour.stats?.totalBookings || 0}
                        </td> */}

                        {/* Created Date Column */}
                        <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {formatDateTime(tour.createdAt)}
                        </td>

                        {/* Actions Column */}
                        <td className='px-4 py-3 text-sm text-right'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='View Details'
                              onClick={() => handleViewClick(tour)}
                            >
                              <Eye
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit Tour'
                              onClick={() => handleEditClick(tour)}
                            >
                              <Edit
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(tour)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Delete Tour'
                            >
                              <Trash2
                                size={16}
                                className='text-muted-foreground hover:text-destructive'
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!showPermissionError && data?.pagination && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing{' '}
                  {(data.pagination.currentPage - 1) *
                    data.pagination.itemsPerPage +
                    1}{' '}
                  to{' '}
                  {Math.min(
                    data.pagination.currentPage * data.pagination.itemsPerPage,
                    data.pagination.totalItems,
                  )}{' '}
                  of {data.pagination.totalItems} tours
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() =>
                      updateFilter(
                        'page',
                        String(data.pagination.currentPage - 1),
                      )
                    }
                    disabled={data.pagination.currentPage === 1}
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(data.pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = data.pagination.currentPage;

                      if (
                        page === 1 ||
                        page === data.pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card hover:bg-accent border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className='text-muted-foreground'>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter(
                        'page',
                        String(data.pagination.currentPage + 1),
                      )
                    }
                    disabled={
                      data.pagination.currentPage === data.pagination.totalPages
                    }
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <DeleteConfirmModal
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          tour={selectedTour}
        />
        <EditTourModal
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          tour={selectedTour}
        />
        <ViewTourModal
          isViewModalOpen={isViewModalOpen}
          setIsViewModalOpen={setIsViewModalOpen}
          tour={selectedTour}
        />
      </div>
    </Layout>
  );
};

export default TourManagement;
